import React, { useEffect, useRef, useState } from 'react';
import Icon from '../AppIcon';

const SUGGESTIONS = [
  'How do I list a property on HireBroker?',
  'How does escrow protection work?',
  'Find rentals on HireBroker',
  'How do I verify as an agent?',
];

const WELCOME =
  'Hi, I am the HireBroker AI assistant. I answer from HireBroker’s own listings and services worldwide — ask about renting, buying, escrow, listing a property, or becoming a verified agent.';

const assistantUrl = () => {
  if (import.meta.env.DEV) {
    return '/assistant/chat';
  }
  return 'https://app.hirebroker.co/api/assistant/chat';
};

const AiAssistantChatbot = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [responseId, setResponseId] = useState(null);
  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'assistant', text: WELCOME },
  ]);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, pending, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async (rawText) => {
    const text = (rawText || '').trim();
    if (!text || pending) {
      return;
    }

    const userMessage = { id: `user-${Date.now()}`, role: 'user', text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setPending(true);

    try {
      const res = await fetch(assistantUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          previous_response_id: responseId,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.error) {
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            text: data.message || 'I could not reach the assistant just now. Please try again.',
          },
        ]);
        return;
      }

      if (data.response_id) {
        setResponseId(data.response_id);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: data.response_id || `asst-${Date.now()}`,
          role: 'assistant',
          text: data.reply,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          text: 'I could not reach the assistant just now. Please try again.',
        },
      ]);
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="fixed right-5 bottom-5 z-[60] flex flex-col items-end gap-3">
      {open && (
        <section
          className="flex h-[min(560px,calc(100vh-7.5rem))] w-[min(380px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          aria-label="HireBroker AI assistant"
        >
          <header className="flex items-center justify-between gap-3 bg-primary px-4 py-3 text-primary-foreground">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                <Icon name="Sparkles" size={18} color="currentColor" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">HireBroker AI</p>
                <p className="truncate text-xs text-white/80">Worldwide property help from this platform</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close assistant"
            >
              <Icon name="X" size={16} color="currentColor" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-6 whitespace-pre-wrap ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-white text-foreground border border-slate-200 rounded-bl-md shadow-sm'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}

            {pending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-muted-foreground shadow-sm">
                  Thinking…
                </div>
              </div>
            )}

            {messages.length === 1 && !pending && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => sendMessage(suggestion)}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <label className="sr-only" htmlFor="hirebroker-assistant-input">
                Ask the HireBroker AI assistant
              </label>
              <textarea
                id="hirebroker-assistant-input"
                ref={inputRef}
                rows={1}
                value={input}
                disabled={pending}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Ask about listings, escrow, or HireBroker…"
                className="max-h-28 min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-primary focus:bg-white"
              />
              <button
                type="submit"
                disabled={pending || !input.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Send message"
              >
                <Icon name="Send" size={16} color="currentColor" />
              </button>
            </div>
          </form>
        </section>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-2xl transition-transform duration-200 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-primary/40"
        aria-label={open ? 'Hide HireBroker AI assistant' : 'Open HireBroker AI assistant'}
        aria-expanded={open}
      >
        <Icon name={open ? 'ChevronDown' : 'Sparkles'} size={22} color="currentColor" />
      </button>
    </div>
  );
};

export default AiAssistantChatbot;
